import {
  CreateOrganizationCustomFieldsMutation,
  CreateOrganizationCustomFieldsMutationVariables,
  GetOrganizationCustomFieldsQuery,
  GetOrganizationCustomFieldsQueryVariables,
  OrganizationCustomFields,
  OrganizationCustomFieldsCreateInput,
  OrganizationCustomFieldsTypes,
  OrganizationCustomFieldsUpdateInput,
  UpdateOrganizationCustomFieldsMutation,
  UpdateOrganizationCustomFieldsMutationVariables,
} from '__generated__/graphql';
import { gql } from 'graphql-request';
import useCustomFieldsEnabled from 'hooks/feature/custom-fields/useCustomFieldsEnabled';
import useGraphQLClient from 'hooks/graphql/useGraphQLClient/useGraphQLClient';
import { first, isNil } from 'lodash';

export default function useOrganizationCustomFields() {
  const client = useGraphQLClient();
  const isCustomFieldsEnabled = useCustomFieldsEnabled();

  async function createOrganizationCustomFields(input: OrganizationCustomFieldsCreateInput) {
    const result = await client.request<
      CreateOrganizationCustomFieldsMutation,
      CreateOrganizationCustomFieldsMutationVariables
    >({
      document: CREATE_ORGANIZATION_CUSTOM_FIELDS,
      variables: {
        organizationCustomFields: input,
      },
    });
    if (isNil(result.createOrganizationCustomFields)) {
      throw Error('Failed to create organization custom fields; received null or undefined response.');
    }
    return result.createOrganizationCustomFields;
  }

  async function updateOrganizationCustomFields(input: OrganizationCustomFieldsUpdateInput) {
    const result = await client.request<
      UpdateOrganizationCustomFieldsMutation,
      UpdateOrganizationCustomFieldsMutationVariables
    >({
      document: UPDATE_ORGANIZATION_CUSTOM_FIELDS,
      variables: {
        organizationCustomFields: input,
      },
    });
    if (isNil(result.updateOrganizationCustomFields)) {
      throw Error(`result was ${result.updateOrganizationCustomFields}`);
    }
    return result.updateOrganizationCustomFields;
  }

  async function getOrganizationCustomFields(
    type: OrganizationCustomFieldsTypes
  ): Promise<OrganizationCustomFields | undefined | null> {
    if (!isCustomFieldsEnabled) {
      return null;
    }
    const result = await client.request<GetOrganizationCustomFieldsQuery, GetOrganizationCustomFieldsQueryVariables>({
      document: ORGANIZATION_CUSTOM_FIELDS_QUERY,
      variables: {
        filter: {
          type: { equal: type },
          deletedOn: { isNull: true },
        },
      },
    });
    return first(result.organizationCustomFields);
  }

  return { createOrganizationCustomFields, updateOrganizationCustomFields, getOrganizationCustomFields };
}

export const CREATE_ORGANIZATION_CUSTOM_FIELDS = gql`
  mutation CreateOrganizationCustomFields($organizationCustomFields: OrganizationCustomFieldsCreateInput) {
    createOrganizationCustomFields(organizationCustomFields: $organizationCustomFields) {
      id
      type
      title1
      title2
      title3
      title4
      title5
    }
  }
`;

export const UPDATE_ORGANIZATION_CUSTOM_FIELDS = gql`
  mutation UpdateOrganizationCustomFields($organizationCustomFields: OrganizationCustomFieldsUpdateInput) {
    updateOrganizationCustomFields(organizationCustomFields: $organizationCustomFields) {
      id
      type
      title1
      title2
      title3
      title4
      title5
    }
  }
`;

export const ORGANIZATION_CUSTOM_FIELDS_QUERY = gql`
  query GetOrganizationCustomFields($filter: OrganizationCustomFieldsFilter, $sort: [OrganizationCustomFieldsSort!]) {
    organizationCustomFields(filter: $filter, sort: $sort) {
      id
      organizationId
      type
      title1
      subtitle1
      adminOnly1
      title2
      subtitle2
      adminOnly2
      title3
      subtitle3
      adminOnly3
      title4
      subtitle4
      adminOnly4
      title5
      subtitle5
      adminOnly5
      createdOn
      updatedOn
      submittedOn
      deletedOn
    }
  }
`;
